import {Injectable, Pipe, PipeTransform} from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {UserService} from './user.service';
import {
  AngularFirestore,
  AngularFirestoreCollection, AngularFirestoreDocument,
  CollectionReference,
  DocumentReference
} from '@angular/fire/compat/firestore';
import {
  AssistanceProvided,
  AssistanceProvidedType,
  ExtensionActivity,
  ExtensionActivityType,
  ExtensionCategory,
  ExtensionEvent, Hort360Definitions, Hort360DefinitionWrapper, Hort360PropertySummary,
  KnownUserType,
  LgaType,
  LocalDefinitions,
  Meeting,
  RiskAddressedType,
  PracticeChange,
  PracticeChangeActivity,
} from '../../../functions/src/types';
import firebase from 'firebase/compat/app';
import {PropertyService} from './property.service';

@Injectable({
  providedIn: 'root'
})
export class DataService {
  public globals: BehaviorSubject<Hort360DefinitionWrapper> = new BehaviorSubject<Hort360DefinitionWrapper>({} as Hort360DefinitionWrapper);
  public globalsTypesRef: AngularFirestoreDocument;

  public knownUsers: BehaviorSubject<KnownUserType[]> = new BehaviorSubject<KnownUserType[]>([]);

  public categories: BehaviorSubject<ExtensionCategory[]> = new BehaviorSubject<ExtensionCategory[]>([]);
  public categoriesRef: AngularFirestoreCollection<ExtensionCategory>;

  public events: BehaviorSubject<ExtensionEvent[]> = new BehaviorSubject<ExtensionEvent[]>([]);
  public eventsRef: AngularFirestoreCollection<ExtensionEvent>;

  public activityTypes: BehaviorSubject<ExtensionActivityType[]> = new BehaviorSubject<ExtensionActivityType[]>([]);
  public activityTypesRef: AngularFirestoreCollection<ExtensionActivityType>;
  public activitiesRef: CollectionReference<ExtensionEvent>;

  public assistanceProvidedTypes: BehaviorSubject<AssistanceProvidedType[]> = new BehaviorSubject<AssistanceProvidedType[]>([]);
  public assistanceProvidedTypesRef: AngularFirestoreCollection<AssistanceProvidedType>;

  public riskAddressedTypes: BehaviorSubject<RiskAddressedType[]> = new BehaviorSubject<RiskAddressedType[]>([]);
  public riskAddressedTypesRef: AngularFirestoreCollection<RiskAddressedType>;

  public localDefinitions: BehaviorSubject<LocalDefinitions> = new BehaviorSubject<LocalDefinitions>({} as LocalDefinitions);
  public localDefinitionsRef: AngularFirestoreDocument;

  public meetings: BehaviorSubject<Meeting[]> = new BehaviorSubject<Meeting[]>([]);
  public meetingsRef: AngularFirestoreCollection<Meeting>;

  public practiceChangeActivities: BehaviorSubject<PracticeChangeActivity[]> = new BehaviorSubject<PracticeChangeActivity[]>([]);
  public practiceChangeActivitiesRef: AngularFirestoreCollection<PracticeChangeActivity>;


  public practiceChangesRef: AngularFirestoreCollection<PracticeChange>;

  public assistanceRef: AngularFirestoreCollection<AssistanceProvided>;

  public contactsRef: AngularFirestoreCollection<ExtensionActivity>;

  public initialLoad: Promise<void> | null;

  constructor(
      private userService: UserService,
      private fbDb: AngularFirestore,
      private propertyService: PropertyService
  ) {
    // Watch Know Users;
    let userSubscription: Subscription;
    let catSubscription: Subscription;
    let eventSubscription: Subscription;
    let activityTypesSubscription: Subscription;
    let globalsSubscription: Subscription;
    let flaggedContacts: Subscription;

    let assistanceProvidedSubscription: Subscription;
    let riskAddressedSubscription: Subscription;

    let localDefinitionsSubscription: Subscription;
    let meetingsSubscription: Subscription;

    let practiceChangeActivitiesSubscription: Subscription;

    this.userService.activeUserState.subscribe(state => {
      // Unsubscribe from previous watcher;
      if (userSubscription) {
        userSubscription.unsubscribe();
      }
      if (catSubscription) {
        catSubscription.unsubscribe();
      }
      if (eventSubscription) {
        eventSubscription.unsubscribe();
      }
      if (activityTypesSubscription) {
        activityTypesSubscription.unsubscribe();
      }
      if (globalsSubscription) {
        globalsSubscription.unsubscribe();
      }
      if (flaggedContacts) {
        flaggedContacts.unsubscribe();
      }

      if (assistanceProvidedSubscription) {
        assistanceProvidedSubscription.unsubscribe();
      }
      if (riskAddressedSubscription) {
        riskAddressedSubscription.unsubscribe();
      }

      if(localDefinitionsSubscription) {
        localDefinitionsSubscription.unsubscribe();
      }

      if(meetingsSubscription) {
        meetingsSubscription.unsubscribe();
      }

      if(practiceChangeActivitiesSubscription) {
        practiceChangeActivitiesSubscription.unsubscribe();
      }

      if (!state) {
        this.knownUsers.next([]);
      } else {
        this.initialLoad = new Promise<void>(resolve => {
          let loading = 0;
          const loaded = () => {
            loading--;
            if (loading <= 0) {
              if (this.initialLoad) {
                resolve();
                this.initialLoad = null;
              }
            }
          };
          loading++;
          userSubscription = this.fbDb.collection<KnownUserType>('users').valueChanges({idField: 'key'}).subscribe(users => {
            this.knownUsers.next(users);
            loaded();
          });

          loading++;
          this.categoriesRef = this.fbDb.collection<ExtensionCategory>('categories');
          catSubscription = this.categoriesRef.valueChanges({idField: 'id'}).subscribe(categories => {
            this.categories.next(categories);
            loaded();
          });

          loading++;
          this.eventsRef = this.fbDb.collection<ExtensionEvent>('events', ref => ref.orderBy('date', 'desc'));
          eventSubscription = this.eventsRef.valueChanges({idField: 'id'}).subscribe(events => {
            this.events.next(events.sort((a, b) => b.date - a.date));
            loaded();
          });

          this.activityTypesRef = this.fbDb.collection<ExtensionActivityType>('activityTypes',
            ref => ref.orderBy('category').orderBy('name')
          );

          loading++;
          activityTypesSubscription = this.activityTypesRef.valueChanges({idField: 'id'}).subscribe(types => {
            this.activityTypes.next(types);
            loaded();
          });


          /*New*/
          this.practiceChangeActivitiesRef = this.fbDb.collection<PracticeChangeActivity>('practiceChangeActivities', ref => ref.orderBy('category').orderBy('name'));

          loading++;
          practiceChangeActivitiesSubscription = this.practiceChangeActivitiesRef.valueChanges({idField: 'id'}).subscribe(activities => {
            this.practiceChangeActivities.next(activities);
            loaded();
          })

          this.practiceChangesRef = this.fbDb.collection<PracticeChange>('practiceChanges', ref => ref.orderBy('date', 'desc'));



          this.assistanceProvidedTypesRef = this.fbDb.collection<AssistanceProvidedType>('assistanceProvidedTypes', ref => ref.orderBy('category').orderBy('name'));

          loading++;
          assistanceProvidedSubscription = this.assistanceProvidedTypesRef.valueChanges({idField: 'id'}).subscribe(types => {
            this.assistanceProvidedTypes.next(types);
            loaded();
          });

          this.riskAddressedTypesRef = this.fbDb.collection<RiskAddressedType>('riskAddressedTypes', ref => ref.orderBy('category').orderBy('name'));

          loading++;
          riskAddressedSubscription = this.riskAddressedTypesRef.valueChanges({idField: 'id'}).subscribe(types => {
            this.riskAddressedTypes.next(types);
            loaded();
          });

          loading++;
          this.localDefinitionsRef = this.fbDb.doc<LocalDefinitions>('globals/localDefinitions');
          localDefinitionsSubscription = this.localDefinitionsRef.valueChanges({id: 'id'}).subscribe(defs => {
            this.localDefinitions.next(defs as LocalDefinitions);
            console.log(defs);
            loaded();
          })

          loading++;
          this.meetingsRef = this.fbDb.collection<Meeting>('meetings', ref => ref.orderBy('date', 'desc'));
          meetingsSubscription = this.meetingsRef.valueChanges({idField: 'id'}).subscribe(meetings => {
            this.meetings.next(meetings.sort((a, b) => b.date - a.date));
            loaded();
          });

          this.assistanceRef = this.fbDb.collection<AssistanceProvided>('assistance',ref => ref.orderBy('date', 'desc'));


          loading++;
          this.globalsTypesRef = this.fbDb.doc<Hort360DefinitionWrapper>('globals/definitions');
          globalsSubscription = this.globalsTypesRef.valueChanges({idField: 'id'}).subscribe(defs => {
            const globals = new Hort360DefinitionWrapper(defs as any as Hort360Definitions);
            console.log(globals);
            this.globals.next(globals);
            loaded();
          });

          if (this.propertyService.initialLoad) {
            loading++;
            this.propertyService.initialLoad.then(a => loaded());
          }

          this.activitiesRef = this.fbDb.collection<ExtensionEvent>('activities').ref;
          this.contactsRef = this.fbDb.collection<ExtensionActivity>('contacts', ref => ref.orderBy('date', 'desc'));

        });
      }
    });
  }


  async addUpdatePracticeChange(practiceChange: PracticeChange): Promise<boolean> {
    try {
      let practiceChangeRef;

      // TODO: Note to self, this probably needs a 500 check?
      const batch = this.fbDb.firestore.batch();
      // Set the parent;
      let actionType;
      let actionId;
      if (practiceChange.id) {
        actionType = AuditLogAction.UPDATE;
        actionId = practiceChange.id;
        practiceChangeRef = this.practiceChangesRef.doc(practiceChange.id).ref;
      } else {
        practiceChangeRef = this.practiceChangesRef.doc().ref;
        actionType = AuditLogAction.NEW;
        // actionId = practiceChange.id;
        practiceChange.id = practiceChangeRef.id;
        actionId = practiceChange.id;
      }

      // Add new attendees
      practiceChange.date = +practiceChange.date;
      practiceChange.user = practiceChange.user || this.userService.activeUserId + ''; // Keep the original even when editing?
      practiceChange.property = practiceChange.property + '';
      practiceChange.notes = practiceChange.notes || '';
      practiceChange.activity = practiceChange.activity || '';
      batch.set(practiceChangeRef, practiceChange);

      // commit
      await batch.commit();
      this.auditLogs(actionType, AuditLogType.PRACTICECHANGE, practiceChangeRef.path, actionId, {practiceChange});
      return true;
    } catch (e) {
      console.error(e);
      throw new Error('Unable to save the practice change at this time: ' + e.message);
    }
  }

  async deletePracticeChange(practiceChangeId): Promise<boolean> {
    try {
      let practiceChangeRef;

      // TODO: Note to self, this probably needs a 500 check?
      const batch = this.fbDb.firestore.batch();

      // Lets rumble!
      practiceChangeRef = this.practiceChangesRef.doc(practiceChangeId).ref;

      // Remove event;
      batch.delete(practiceChangeRef);

      // commit
      await batch.commit();
      this.auditLogs(AuditLogAction.DELETE, AuditLogType.PRACTICECHANGE, practiceChangeRef.path, practiceChangeId, {});
      return true;
    } catch (e) {
      console.error(e);
      throw new Error('Unable to delete the practice change at this time: ' + e.message);
    }
  }


  async addUpdateAssistance(assistance: AssistanceProvided): Promise<boolean> {
    try {
      let assistanceRef;

      // TODO: Note to self, this probably needs a 500 check?
      const batch = this.fbDb.firestore.batch();

      // Set the parent;
      let actionType;
      let actionId;
      if (assistance.id) {
        actionType = AuditLogAction.UPDATE;
        actionId = assistance.id;
        assistanceRef = this.assistanceRef.doc(assistance.id).ref;
      } else {
        assistanceRef = this.assistanceRef.doc().ref;
        actionType = AuditLogAction.NEW;
        actionId = assistanceRef.id;
      }

      // Add new attendees
      assistance.date = +assistance.date;
      assistance.id = actionId;
      assistance.user = assistance.user || this.userService.activeUserId + ''; // Keep the original even when editing?
      assistance.property = assistance.property + '';
      batch.set(assistanceRef, assistance);

      // commit
      await batch.commit();
      this.auditLogs(actionType, AuditLogType.ASSISTANCE, assistanceRef.path, actionId, {assistance});
      return true;
    } catch (e) {
      console.error(e);
      throw new Error('Unable to save contact at this time: ' + e.message);
    }
  }

  async deleteAssistance(assistanceId): Promise<boolean> {
    try {
      let assistanceRef;

      // TODO: Note to self, this probably needs a 500 check?
      const batch = this.fbDb.firestore.batch();

      // Lets rumble!
      assistanceRef = this.assistanceRef.doc(assistanceId).ref;

      // Remove event;
      batch.delete(assistanceRef);

      // commit
      await batch.commit();
      this.auditLogs(AuditLogAction.DELETE, AuditLogType.ASSISTANCE, assistanceRef.path, assistanceId, {});
      return true;
    } catch (e) {
      console.error(e);
      throw new Error('Unable to delete event at this time: ' + e.message);
    }
  }


  async addUpdateContact(contact: ExtensionActivity): Promise<boolean> {
    try {
      let contactRef;

      // TODO: Note to self, this probably needs a 500 check?
      const batch = this.fbDb.firestore.batch();

      // Set the parent;
      let actionType;
      let actionId;
      if (contact.id) {
        actionType = AuditLogAction.UPDATE;
        actionId = contact.id;
        contactRef = this.contactsRef.doc(contact.id).ref;
      } else {
        contactRef = this.contactsRef.doc().ref;
        actionType = AuditLogAction.NEW;
        actionId = contactRef.id;
      }

      // Add new attendees
      contact.date = +contact.date;
      contact.id = actionId;
      contact.user = contact.user || this.userService.activeUserId + ''; // Keep the original even when editing?
      contact.property = contact.property + '';
      batch.set(contactRef, contact);

      // commit
      await batch.commit();
      this.auditLogs(actionType, AuditLogType.CONTACT, contactRef.path, actionId, { contact });
      return true;
    } catch (e) {
      console.error(e);
      throw new Error('Unable to save contact at this time: ' + e.message);
    }
  }

  async deleteContact(contactId): Promise<boolean> {
    try {
      let contactRef;

      // TODO: Note to self, this probably needs a 500 check?
      const batch = this.fbDb.firestore.batch();

      // Lets rumble!
      contactRef = this.contactsRef.doc(contactId).ref;

      // Remove event;
      batch.delete(contactRef);

      // commit
      await batch.commit();
      this.auditLogs(AuditLogAction.DELETE, AuditLogType.EVENT, contactRef.path, contactId, {});
      return true;
    } catch (e) {
      console.error(e);
      throw new Error('Unable to delete event at this time: ' + e.message);
    }
  }


  async addUpdateEvent(event: ExtensionEvent, attendees: {[key: number]: number}): Promise<boolean> {
    try {
      let eventRef;
      const attendeeIds = Object.keys(attendees);

      // TODO: Note to self, this probably needs a 500 check?
      const batch = this.fbDb.firestore.batch();

      // Set the parent;
      let actionType;
      let actionId;
      if (event.id) {
        actionType = AuditLogAction.UPDATE;
        actionId = event.id;
        eventRef = this.eventsRef.doc(event.id).ref;

        // Remove old attendees
        const oldAttendees = await eventRef.collection('attendees').get();
        oldAttendees.docs.forEach(d => {
          // Is it missing from our new attendees?
          if (!attendeeIds.find(id => id + '' === d.id + '')) {
            batch.delete(d.ref);
          }
        });
      } else {
        eventRef = this.eventsRef.doc().ref;
        actionType = AuditLogAction.NEW;
        actionId = eventRef.id;
      }

      // Add new attendees
      event.date = +event.date;
      event.user = event.user || this.userService.activeUserId + ''; // Keep the original even when editing?
      event.properties = attendeeIds.length;
      event.attendees = attendeeIds.map(id => attendees[id] || 0).reduce((p, c) => p + c, 0);
      batch.set(eventRef, event);
      attendeeIds.forEach(id => batch.set(eventRef.collection('attendees').doc(id + ''), { id, total: attendees[id] }));

      // commit
      await batch.commit();
      this.auditLogs(actionType, AuditLogType.EVENT, eventRef.path, actionId, {event, attendees});
      return true;
    } catch (e) {
      console.error(e);
      throw new Error('Unable to save event at this time: ' + e.message);
    }
  }

  async deleteEvent(eventId): Promise<boolean> {
    try {
      let eventRef;

      // TODO: Note to self, this probably needs a 500 check?
      const batch = this.fbDb.firestore.batch();

      // Lets rumble!
      eventRef = this.eventsRef.doc(eventId).ref;

      // Remove old attendees
      const oldAttendees = await eventRef.collection('attendees').get();
      oldAttendees.docs.forEach(d => batch.delete(d.ref));

      // Remove event;
      batch.delete(eventRef);

      // commit
      await batch.commit();
      this.auditLogs(AuditLogAction.DELETE, AuditLogType.EVENT, eventRef.path, eventId, {});
      return true;
    } catch (e) {
      console.error(e);
      throw new Error('Unable to delete event at this time: ' + e.message);
    }
  }

  async addRemoveEventAttendee(eventId: string, propertyId: number | string, addProperty: boolean = true, addAttendees: number = 1): Promise<boolean> {
    try {
      let eventRef: DocumentReference<ExtensionEvent>;
      const batch = this.fbDb.firestore.batch();

      // Lets rumble!
      eventRef = this.eventsRef.doc(eventId).ref;
      const eventDoc = await eventRef.get();
      if (!eventDoc.exists) {
        throw new Error('Event does not exist.');
      }

      // Attendees
      const attendeesRef = eventRef.collection('attendees');
      const attendeeRef = await attendeesRef.doc(propertyId + '');
      const attendeeDoc = await attendeeRef.get();

      // Are we adding?
      if (addProperty) {
        if (attendeeDoc.exists) {
          throw new Error(`Property ${propertyId} already an attendee of event.`);
        }
        batch.set(attendeeRef, {id: propertyId, total: addAttendees });
      } else {
        // Remove
        if (!attendeeDoc.exists) {
          throw new Error(`Property ${propertyId} is not an attendee of event.`);
        }
        addAttendees = (attendeeDoc.data().total || 0) * -1; // Remove attendees!
        batch.delete(attendeeRef);
      }
      // TODO: BATCH + increment === NO GO?
      // batch.set(eventRef, { attendees: firebase.firestore.FieldValue.increment(addProperty ? 1 : -1) } as any, { mergeFields: ['attendees'] });

      const eventDocData = eventDoc.data();
      batch.set(eventRef, { attendees: eventDocData.attendees + addAttendees, properties: eventDocData.properties + (addProperty ? 1 : -1) }, { mergeFields: ['attendees', 'properties'] });

      // commit
      await batch.commit();
      this.auditLogs(AuditLogAction.UPDATE, AuditLogType.EVENT, eventRef.path, eventId, { addProperty, propertyId });
      return true;
    } catch (e) {
      console.error(e);
      throw new Error('Unable to complete: ' + e.message);
    }
  }


  async addUpdateMeeting(meeting: Meeting, attendees: {[key: number]: number}): Promise<boolean> {
    try {
      let meetingRef;
      const attendeeIds = Object.keys(attendees);

      // TODO: Note to self, this probably needs a 500 check?
      const batch = this.fbDb.firestore.batch();

      // Set the parent;
      let actionType;
      let actionId;
      if (meeting.id) {
        actionType = AuditLogAction.UPDATE;
        actionId = meeting.id;
        meetingRef = this.meetingsRef.doc(meeting.id).ref;

        // Remove old attendees
        const oldAttendees = await meetingRef.collection('attendees').get();
        oldAttendees.docs.forEach(d => {
          // Is it missing from our new attendees?
          if (!attendeeIds.find(id => id + '' === d.id + '')) {
            batch.delete(d.ref);
          }
        });
      } else {
        meetingRef = this.meetingsRef.doc().ref;
        actionType = AuditLogAction.NEW;
        actionId = meetingRef.id;
      }

      // Add new attendees
      meeting.date = +meeting.date;
      meeting.user = meeting.user || this.userService.activeUserId + ''; // Keep the original even when editing?
      meeting.properties = attendeeIds.length;
      meeting.attendees = attendeeIds.map(id => attendees[id] || 0).reduce((p, c) => p + c, 0);
      batch.set(meetingRef, meeting);
      attendeeIds.forEach(id => batch.set(meetingRef.collection('attendees').doc(id + ''), { id, total: attendees[id] }));

      // commit
      await batch.commit();
      this.auditLogs(actionType, AuditLogType.MEETING, meetingRef.path, actionId, {meeting, attendees});
      return true;
    } catch (e) {
      console.error(e);
      throw new Error('Unable to save meeting at this time: ' + e.message);
    }
  }

  async deleteMeeting(meetingId): Promise<boolean> {
    try {
      let meetingRef;

      // TODO: Note to self, this probably needs a 500 check?
      const batch = this.fbDb.firestore.batch();

      meetingRef = this.meetingsRef.doc(meetingId).ref;

      // Remove old attendees
      const oldAttendees = await meetingRef.collection('attendees').get();
      oldAttendees.docs.forEach(d => batch.delete(d.ref));

      // Remove event;
      batch.delete(meetingRef);

      // commit
      await batch.commit();
      this.auditLogs(AuditLogAction.DELETE, AuditLogType.MEETING, meetingRef.path, meetingId, {});
      return true;
    } catch (e) {
      console.error(e);
      throw new Error('Unable to delete meeting at this time: ' + e.message);
    }
  }

  async addRemoveMeetingAttendee(meetingId: string, propertyId: number | string, addProperty: boolean = true, addAttendees: number = 1): Promise<boolean> {
    try {
      let meetingRef: DocumentReference<Meeting>;
      const batch = this.fbDb.firestore.batch();

      // Lets rumble!
      meetingRef = this.meetingsRef.doc(meetingId).ref;
      const eventDoc = await meetingRef.get();
      if (!eventDoc.exists) {
        throw new Error('Meeting does not exist.');
      }

      // Attendees
      const attendeesRef = meetingRef.collection('attendees');
      const attendeeRef = await attendeesRef.doc(propertyId + '');
      const attendeeDoc = await attendeeRef.get();

      // Are we adding?
      if (addProperty) {
        if (attendeeDoc.exists) {
          throw new Error(`Property ${propertyId} already an attendee of meeting.`);
        }
        batch.set(attendeeRef, {id: propertyId, total: addAttendees });
      } else {
        // Remove
        if (!attendeeDoc.exists) {
          throw new Error(`Property ${propertyId} is not an attendee of meeting.`);
        }
        addAttendees = (attendeeDoc.data().total || 0) * -1; // Remove attendees!
        batch.delete(attendeeRef);
      }
      // TODO: BATCH + increment === NO GO?
      // batch.set(eventRef, { attendees: firebase.firestore.FieldValue.increment(addProperty ? 1 : -1) } as any, { mergeFields: ['attendees'] });

      const eventDocData = eventDoc.data();
      batch.set(meetingRef, { attendees: eventDocData.attendees + addAttendees, properties: eventDocData.properties + (addProperty ? 1 : -1) }, { mergeFields: ['attendees', 'properties'] });

      // commit
      await batch.commit();
      this.auditLogs(AuditLogAction.UPDATE, AuditLogType.MEETING, meetingRef.path, meetingId, { addProperty, propertyId });
      return true;
    } catch (e) {
      console.error(e);
      throw new Error('Unable to complete: ' + e.message);
    }
  }


  // Simple audit trail. This could be done by backend OnChange, but overkill as our users are not trying to break the system (we hope!?)!!
  async auditLogs(action: AuditLogAction, type: AuditLogType, path: string, id: string, params: any) {
    try {
      await this.fbDb.collection('logs').add({
        action,
        type,
        path,
        id,
        ts: firebase.firestore.FieldValue.serverTimestamp(),
        user: this.userService.activeUserId + '',
        params
      });
    } catch (e) {
      console.log(e);
    }
  }

  loginToHort360Link() {
    let url = 'https://hort360.hort360.com.au';
    if (this.userService.hasActiveUser && this.userService.activeUserClaims?.id) {
      url += `#!/f/${this.userService.activeUserClaims.id}/f/t/?t=` + encodeURIComponent(this.userService.activeUserClaims?.token);
    }
    return url;
  }

  loginToHort360GrowerLink(growerId: number) {
    if (!this.userService.hasActiveUser) {
      throw new Error('No user.');
    }
    return `https://hort360.hort360.com.au/#!/f/${growerId}/t/?t=` + encodeURIComponent(this.userService.activeUserClaims?.token);
  }

  async addUpdateOtherProperty(other: Hort360PropertySummary): Promise<boolean> {
    try {
      let ref;

      // TODO: Note to self, this probably needs a 500 check?
      const batch = this.fbDb.firestore.batch();

      // Set the parent;
      let actionType;
      let actionId;
      if (other.id) {
        actionType = AuditLogAction.UPDATE;
        actionId = other.id;
        ref = this.propertyService.summaryContactsRef.doc(other.id + '').ref;
      } else {
        ref = this.propertyService.summaryContactsRef.doc().ref;
        actionType = AuditLogAction.NEW;
        actionId = ref.id;
      }

      // Add new attendees
      other.id = actionId;
      batch.set(ref, other);

      // commit
      await batch.commit();
      this.auditLogs(actionType, AuditLogType.OTHERPROP, ref.path, actionId, { other });
      return true;
    } catch (e) {
      console.error(e);
      throw new Error('Unable to save contact at this time: ' + e.message);
    }
  }


}

export enum AuditLogAction {
  DELETE = 'delete',
  UPDATE = 'update',
  NEW = 'new',
  ACTION = 'action'
}

export enum AuditLogType {
  EVENT = 'event',
  MEETING = 'meeting',
  CONTACT = 'contact',
  ASSISTANCE = 'assistance',
  OTHERPROP = 'otherprop',
  PRACTICECHANGE = 'practicechange',

  FAILED_LOGIN = 'failed_login',
  SUCCESSFUL_LOGIN = 'successful_login',
  AUTH_AS_GROWER = 'auth_as_grower',
  AUTH_AS_USER = 'auth_as_user'
}


@Pipe({
  name: 'resolveAssistanceType'
})
export class ResolveAssistanceType implements PipeTransform  {
  constructor(private dataService: DataService) {}
  transform(assistanceTypeId:string): AssistanceProvidedType {
    return this.dataService.assistanceProvidedTypes.getValue().find(type => type.id === assistanceTypeId) || {} as AssistanceProvidedType;
  }
}

@Pipe({
  name: 'resolvePracticeChangeActivity'
})
export class ResolvePracticeChangeActivity implements PipeTransform  {
  constructor(private dataService: DataService) {}
  transform(practiceChangeActivityId:string): PracticeChangeActivity {
    return this.dataService.practiceChangeActivities.getValue().find(type => type.id === practiceChangeActivityId) || {} as PracticeChangeActivity;
  }
}

@Pipe({
  name: 'resolveLga'
})
export class ResolveLga implements PipeTransform  {
  constructor(private dataService: DataService) {}
  transform(lgaId:string): LgaType {
    return this.dataService.localDefinitions.getValue().lga.find(lga => lga.id === lgaId) || {} as LgaType;
  }
}

@Pipe({
  name: 'resolveRiskType'
})
export class ResolveRiskType implements PipeTransform  {
  constructor(private dataService: DataService) {}
  transform(riskTypeId:string): RiskAddressedType {
    return this.dataService.riskAddressedTypes.getValue().find(risk => risk.id === riskTypeId) || {} as RiskAddressedType;
  }
}

@Pipe({
  name: 'resolveCategory'
})
export class ResolveCategory implements PipeTransform  {
  constructor(private dataService: DataService) { }
  transform(catId: string): ExtensionCategory {
    return this.dataService.categories.getValue().find(cat => cat.id === catId) || {} as ExtensionCategory;
  }
}

@Pipe({
  name: 'resolveUser'
})
export class ResolveUser implements PipeTransform  {
  constructor(private dataService: DataService) { }
  transform(userId: string): KnownUserType {
    return this.dataService.knownUsers.getValue().find(user => ((user.key === userId) || (user.uid === userId))) || {} as KnownUserType;
  }
}
